import { useEffect } from 'react'

import { useUnit } from 'effector-react'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $allowedFanNumbering } from '@/models/chattingUI/chats/chats.ts'
import { openDialog } from '@/models/dialogs'
import { useChattingTabs } from '@/modules/chatting/state/tabs-provider.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { ToggleGroup } from '@/modules/chatting/ui/components/toggle-group'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'

import { useSizeObserver } from '../../sidebar/use-size-observer'
import { TabItem } from './tab-item'

const MAX_TAB_WIDTH = 150
const PLUS_BUTTON_WIDTH = 56

export const Tabs = () => {
  const {
    tabs,
    openNewTab,
    changeActiveTab,
    isTabsLimitReached,
    activeTabIndex,
    closeTab,
  } = useChattingTabs()
  const allowedFanNumbering = useUnit($allowedFanNumbering)
  const [tabsBarRef, tabsBarWidth] = useSizeObserver()
  const { maxFanNumber } = useSettingsContext()

  const handleTabChange = (tabIndex: string) => {
    if (!tabIndex) return

    changeActiveTab(+tabIndex)
  }

  useEffect(() => {
    if (!allowedFanNumbering.length) {
      return
    }

    const shouldCloseTab = (tab: (typeof tabs)[0]): boolean => {
      if (tab.fanId === -1) return false

      return !allowedFanNumbering.includes(tab.fanNumber!)
    }

    const tabIndicesToClose = tabs.reduce<number[]>((indexes, tab, index) => {
      if (shouldCloseTab(tab)) {
        indexes.push(index)
      }

      return indexes
    }, [])

    if (tabIndicesToClose.length === tabs.length) {
      openNewTab()
    }

    tabIndicesToClose.forEach(closeTab)
  }, [allowedFanNumbering, tabs, maxFanNumber, closeTab, openNewTab])

  const handleClickMobileTabsDialog = () => {
    openDialog({
      type: DIALOG_TYPE.MOBILE_TABS,
    })
  }

  const potentialTabsContainerWidth = tabsBarWidth - PLUS_BUTTON_WIDTH
  const tabItemWidth = Math.min(
    potentialTabsContainerWidth / tabs.length,
    MAX_TAB_WIDTH,
  )

  return (
    <div
      className="flex flex-1 items-center overflow-hidden"
      ref={tabsBarRef}
    >
      <div className="hidden flex-1 items-center overflow-hidden md:flex">
        <div className="max-w-max flex-1 overflow-hidden">
          <ToggleGroup
            type="single"
            value={String(activeTabIndex)}
            onValueChange={handleTabChange}
            className="max-w-full gap-0.5"
          >
            {tabs.map((tab, index) => (
              <TabItem
                tab={tab}
                key={index} // eslint-disable-line react/no-array-index-key
                index={index}
                width={tabItemWidth}
              />
            ))}
          </ToggleGroup>
        </div>
        <IconButton
          icon={PlusIcon}
          iconClassName="h-7 w-7"
          onClick={() => openNewTab()}
          disabled={isTabsLimitReached}
          tooltip="New Tab"
          className="hover:bg-primary-50 hover:text-title dark:hover:bg-secondary-800 mx-2 w-max shrink-0 cursor-pointer rounded border border-transparent p-[5px] dark:hover:text-white"
        />
      </div>
      <div
        onClick={handleClickMobileTabsDialog}
        className="border-title hover:border-primary-500 hover:text-primary-500 ml-auto mr-3 flex h-5 w-5 cursor-pointer items-center justify-center rounded border-2 text-[10px] font-semibold md:hidden"
      >
        {tabs.length}
      </div>
    </div>
  )
}
